import React, { useId, useRef, useState } from 'react';
import { t, useI18n } from '../../i18n';
import { useDismiss } from '../../hooks/useDismiss';
import {
  MIN_PLACES_QUERY_LENGTH,
  googlePlacesService,
  type PlaceDetails,
  type PlacePrediction,
} from '../../services/googlePlacesService';
import { getCountryName, getFlagSrc } from './countries';
import { useAddressSearch } from './useAddressSearch';

interface AddressSearchProps {
  /** Results are limited to this country; null searches everywhere. */
  countryCode: string | null;
  /** Called with a place that has a location. */
  onSelect: (place: PlaceDetails) => void;
  onPickOnMap: () => void;
}

/**
 * Type-ahead address search for the site builder (an ARIA list-autocomplete
 * combobox). Every state says what is happening -- searching, nothing found,
 * search unavailable -- and each points at the map as the way through, so a
 * failed lookup is never a silent empty box.
 */
export default function AddressSearch({ countryCode, onSelect, onPickOnMap }: AddressSearchProps) {
  useI18n();
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  const scopeId = `${baseId}-scope`;
  const errorId = `${baseId}-error`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  const search = useAddressSearch(query, countryCode);
  useDismiss(rootRef, isOpen, () => setIsOpen(false));

  const predictions = search.status === 'done' || search.status === 'searching'
    ? search.predictions
    : [];
  const hasQuery = query.trim().length >= MIN_PLACES_QUERY_LENGTH;
  const isBusy = search.status === 'searching' || isResolving;
  const countryName = countryCode ? getCountryName(countryCode) ?? countryCode : null;

  let statusMessage = '';
  if (search.status === 'error') {
    statusMessage = t('Address search is unavailable right now.');
  } else if (search.status === 'done' && predictions.length === 0) {
    statusMessage = countryName
      ? t('No addresses match “{query}” in {country}.', { query: query.trim(), country: countryName })
      : t('No addresses match “{query}”.', { query: query.trim() });
  } else if (search.status === 'searching' && predictions.length === 0) {
    statusMessage = t('Searching…');
  }
  const showMapFallback = search.status === 'error'
    || (search.status === 'done' && predictions.length === 0);
  const showPopover = isOpen && hasQuery && (predictions.length > 0 || statusMessage !== '');
  const active = activeIndex < predictions.length ? activeIndex : -1;

  const choose = async (prediction: PlacePrediction) => {
    setIsOpen(false);
    setIsResolving(true);
    setResolveError('');
    try {
      const details = await googlePlacesService.getPlaceDetails(prediction.placeId);
      if (!details || details.latitude == null || details.longitude == null) {
        throw new Error('The place has no location');
      }
      onSelect({
        ...details,
        formattedAddress: details.formattedAddress || prediction.description,
        name: details.name || prediction.mainText,
      });
      setQuery('');
      setActiveIndex(-1);
    } catch (err) {
      console.error('Error getting place details:', (err as Error).message);
      setResolveError(t('That address could not be loaded. Try again, or drop the pin on the map.'));
    } finally {
      setIsResolving(false);
    }
  };

  const pickOnMap = () => {
    setIsOpen(false);
    onPickOnMap();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const count = predictions.length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setIsOpen(true);
        if (count) setActiveIndex((active + 1) % count);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setIsOpen(true);
        if (count) setActiveIndex((active - 1 + count) % count);
        break;
      case 'Enter':
        // Never submits the step: Enter here means "pick", not "continue".
        event.preventDefault();
        if (showPopover && count) void choose(predictions[active >= 0 ? active : 0]);
        break;
      case 'Escape':
        // Closes the list, then clears the box; only an idle Escape reaches
        // the builder, which treats it as "leave".
        if (showPopover) {
          event.stopPropagation();
          setIsOpen(false);
        } else if (query) {
          event.stopPropagation();
          setQuery('');
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className="sb-field">
      <label className="sb-field__label" htmlFor={inputId}>{t('Search Address')}</label>
      <div className="sb-search">
        <div className="sb-search__wrap" ref={rootRef}>
          <input
            id={inputId}
            type="text"
            role="combobox"
            className="sb-input sb-search__input"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActiveIndex(-1);
              setResolveError('');
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={t('Start typing an address or place…')}
            autoComplete="off"
            spellCheck={false}
            aria-expanded={showPopover}
            aria-controls={predictions.length > 0 ? listboxId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={showPopover && active >= 0 ? optionId(active) : undefined}
            aria-describedby={resolveError ? errorId : scopeId}
            aria-busy={isBusy || undefined}
          />
          {isBusy && <span className="sb-search__spinner" aria-hidden="true" />}

          {showPopover && (
            <div className="sb-predict">
              {predictions.length > 0 && (
                <ul
                  id={listboxId}
                  role="listbox"
                  aria-label={t('Address suggestions')}
                  className="sb-predict__list"
                  // Keeps focus in the input so the click is not read as leaving.
                  onMouseDown={(event) => event.preventDefault()}
                >
                  {predictions.map((prediction, index) => (
                    <li
                      key={prediction.placeId}
                      id={optionId(index)}
                      role="option"
                      aria-selected={index === active}
                      className={`sb-predict__item${index === active ? ' sb-predict__item--active' : ''}`}
                      onMouseMove={() => { if (index !== active) setActiveIndex(index); }}
                      onClick={() => { void choose(prediction); }}
                    >
                      <span className="sb-predict__main">{prediction.mainText}</span>
                      {prediction.secondaryText && (
                        <span className="sb-predict__secondary">{prediction.secondaryText}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {statusMessage && (
                <div
                  className={`sb-predict__status${search.status === 'error' ? ' sb-predict__status--error' : ''}`}
                  role="status"
                >
                  <span>{statusMessage}</span>
                  {showMapFallback && (
                    <button type="button" className="sb-predict__action" onClick={pickOnMap}>
                      {t('Drop the pin on the map instead')}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <button type="button" className="sb-btn" onClick={pickOnMap}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2a7 7 0 017 7c0 5-7 13-7 13S5 14 5 9a7 7 0 017-7z" /><circle cx="12" cy="9" r="2.5" /></svg>
          {t('Pick on Map')}
        </button>
      </div>

      {resolveError ? (
        <p className="sb-search__error" id={errorId} role="alert">{resolveError}</p>
      ) : (
        <p className="sb-hint sb-search__scope" id={scopeId}>
          {countryCode && countryName ? (
            <>
              <img className="sb-country__flag" src={getFlagSrc(countryCode)} alt="" />
              {t('Searching in {country}. Change the country above to search elsewhere.', { country: countryName })}
            </>
          ) : t('Searching every country. Choose a country above to narrow it down.')}
        </p>
      )}
    </div>
  );
}
